import type {
  CargoItemKindValue,
  CargoTypeValue,
  OrderTaskCargoItem,
  TaskTypeValue,
} from '@my/tms-api';
import type {
  CargoFamily,
  CargoItemDraft,
  CargoPick,
  CargoProductLineDraft,
  PalletVariantOption,
} from './types';

/** Cargo types where the picker expands into one option per variant. */
export const VARIANT_EXPANDED_TYPES: ReadonlySet<CargoTypeValue> = new Set([
  'IsoTank',
  'Container',
]);

export function buildCargoPick(definitionId: string, variantId: string | null): string {
  return variantId ? `${definitionId}:${variantId}` : definitionId;
}

export function parseCargoPick(raw: string): CargoPick | null {
  if (!raw) return null;
  const sep = raw.indexOf(':');
  if (sep < 0) return { definitionId: raw, variantId: null };
  return { definitionId: raw.slice(0, sep), variantId: raw.slice(sep + 1) };
}

/**
 * Allowed task types per cargo type — mirrors the BE catalogue. Each
 * list is sorted by operational likelihood: cargo-handling first,
 * then Booking (synthetic, spawns paired BookingDeparture +
 * BookingArrival via its own flow), then ancillary process events.
 */
export const CARGO_TASK_TYPES: Record<string, string[]> = {
  FullLoad: ['Collect', 'Deliver', 'Booking', 'Stop', 'Instruction'],
  PartLoad: ['Collect', 'Deliver', 'Booking', 'Stop', 'Instruction'],
  IsoTank: [
    'PickUp',
    'Load',
    'Unload',
    'DropOff',
    'Booking',
    'Wash',
    'Stop',
    'Instruction',
    'Inspection',
  ],
  Container: [
    'PickUp',
    'Load',
    'Unload',
    'DropOff',
    'Booking',
    'Stop',
    'Instruction',
    'Inspection',
  ],
  Pallet: ['Collect', 'Deliver', 'Booking', 'PalletExchange', 'Stop', 'Instruction'],
  BulkMaterials: [
    'Load',
    'Unload',
    'Booking',
    'Weigh',
    'SampleCheck',
    'Stop',
    'Instruction',
    'Inspection',
  ],
  BulkLiquids: [
    'Load',
    'Unload',
    'Booking',
    'Weigh',
    'SampleCheck',
    'TankClean',
    'Stop',
    'Instruction',
    'Inspection',
  ],
  Livestock: [
    'Collect',
    'Deliver',
    'Booking',
    'FeedWaterStop',
    'VetCheck',
    'BorderInspection',
    'Stop',
    'Instruction',
  ],
  Fmcg: ['Collect', 'Deliver', 'Booking', 'TempCheck', 'Stop', 'Instruction'],
  AgriGrains: [
    'Load',
    'Unload',
    'Booking',
    'Weigh',
    'FumigationStop',
    'PhytosanitaryCheck',
    'Stop',
    'Instruction',
  ],
  Construction: [
    'Collect',
    'Deliver',
    'Booking',
    'PermitStop',
    'EscortStart',
    'SiteCheck',
    'Stop',
    'Instruction',
  ],
  TemperatureControlled: [
    'Collect',
    'Deliver',
    'Booking',
    'TempCheck',
    'ReeferInspection',
    'Stop',
    'Instruction',
  ],
};

/**
 * Cargo-handling task types — the only ones that own OrderTaskCargoItem
 * rows. Process-only events (Stop / Instruction / Inspection / Wash /
 * Weigh / …) have no cargo state to capture.
 */
export const CARGO_HANDLING_TASK_TYPES: ReadonlySet<TaskTypeValue> = new Set<TaskTypeValue>([
  'PickUp',
  'DropOff',
  'Collect',
  'Deliver',
  'Load',
  'Unload',
]);

/**
 * Process-event task types that don't carry a per-stop reference —
 * Stop / Instruction / Inspection are pure annotations on the timeline.
 * Every other task type (cargo-handling legs + booking halves) gets a
 * Reference field on its form.
 */
export const NO_REFERENCE_TASK_TYPES: ReadonlySet<TaskTypeValue> = new Set<TaskTypeValue>([
  'Stop',
  'Instruction',
  'Inspection',
]);

/** True when the task type should surface a per-stop Reference field. */
export function taskTypeSupportsReference(taskType: TaskTypeValue): boolean {
  return !NO_REFERENCE_TASK_TYPES.has(taskType);
}

export const EMPTY_CARGO_ITEM_DRAFT: CargoItemDraft = {
  identifier: '',
  seal: '',
  sealStatus: '',
  tankStatus: '',
  capacity: null,
  weightKg: null,
  volumeM3: null,
  loadingMetresM: null,
  description: '',
  quantity: null,
  products: [],
};

/** Inverse of `toOrderTaskCargoItem...` save builders — collapses BE nulls to empty sentinels. */
export function fromOrderTaskCargoItem(item: OrderTaskCargoItem): CargoItemDraft {
  return {
    id: item.id,
    itemIndex: item.itemIndex,
    identifier: item.identifier ?? '',
    seal: item.seal ?? '',
    sealStatus: item.sealStatus ?? '',
    tankStatus: item.tankStatus ?? '',
    capacity: item.capacity ?? null,
    weightKg: item.weightKg ?? null,
    volumeM3: item.volumeM3 ?? null,
    loadingMetresM: item.loadingMetresM ?? null,
    description: item.description ?? '',
    quantity: item.quantity ?? null,
    products: productLinesFromItem(item),
  };
}

/** A saved unit's product lines → editor lines, in compartment order. */
export function productLinesFromItem(item: OrderTaskCargoItem): CargoProductLineDraft[] {
  return (item.products ?? [])
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map((p) => ({
      key: p.id,
      id: p.id,
      productId: p.productId,
      productName: p.productName,
      amount: p.weightKg ?? (p.volumeM3 != null ? Math.round(p.volumeM3 * 1000) : null),
      unit: p.weightKg == null && p.volumeM3 != null ? 'L' : 'kg',
    }));
}

/**
 * Loading metres to persist for a cargo line. When the row's description
 * matches a pallet variant that carries `loadingMetres`, it's the derived
 * total (per-pallet metres × quantity) — saved so it can be overridden later.
 * Otherwise (Full Load / Part Load …) it's the manual value entered on the row.
 */
export function resolveLoadingMetres(
  draft: CargoItemDraft,
  palletVariants: PalletVariantOption[],
): number | null {
  const variant = palletVariants.find((v) => v.name === draft.description);
  if (variant?.loadingMetres != null && draft.quantity != null) {
    return Number((variant.loadingMetres * draft.quantity).toFixed(2));
  }
  return draft.loadingMetresM;
}

/**
 * Which handling family a cargo type belongs to — what the cargo-item row for
 * it actually IS. A container is a box with a BIC code, a tank (including the
 * bulk liquid / bulk material types, which move in the same barrels) is a unit
 * with a Tank ID; everything else is a line of goods with a description and a
 * quantity.
 *
 * <p>
 * Lifted out of `OrderDetailsPage`, which computed it inline, once the orders
 * LIST needed the same answer to decide whether a row's identifying detail is
 * an identifier or an item summary. Two copies would have drifted, and the
 * symptom — a list showing item descriptions for a cargo type whose form
 * captures a BIC code — is the kind nobody reports as a bug.
 * </p>
 */
export function cargoFamilyFor(cargoType: CargoTypeValue | undefined | null): CargoFamily {
  if (cargoType === 'Container') return 'container';
  if (cargoType === 'IsoTank' || cargoType === 'BulkLiquids' || cargoType === 'BulkMaterials') {
    return 'tank';
  }
  return 'pallet';
}

/**
 * Families whose cargo item IS a physical, re-usable unit — a container
 * box or a tank barrel. The unit travels the whole job, so its
 * identifier (BIC code / Tank ID) is the same at every stop, unlike the
 * per-stop measurements recorded alongside it.
 *
 * <p>
 * Lives here rather than in `cargoCascade.ts` (its original home) because
 * `taskTypeOwnsCargoItems` below needs it, and the cascade module already
 * imports from this one — the other direction would be a cycle.
 * `cargoCascade.ts` re-exports it, so existing importers are unaffected.
 * </p>
 */
export const UNIT_IDENTIFIER_FAMILIES: ReadonlySet<CargoFamily> = new Set<CargoFamily>([
  'container',
  'tank',
]);

/** What the identifier column is called on screen, for dialog copy. */
export function unitIdentifierLabel(family: CargoFamily): string {
  return family === 'tank' ? 'Tank ID' : 'BIC code';
}

/**
 * Whether a task of this type should capture cargo-item rows at all.
 *
 * <p>
 * Pallet-family cargo is a LINE OF GOODS, which only exists at the legs
 * that pick it up or set it down — a Stop or an Inspection has no
 * quantity to record, so those types render nothing.
 * </p>
 *
 * <p>
 * Container and tank cargo is a PHYSICAL UNIT that exists for the whole
 * job. A wash, an inspection, a weigh, a border check — each happens TO a
 * specific barrel, and the operator needs to say which one. So every task
 * type carries the unit row for those families, and
 * `CARGO_HANDLING_TASK_TYPES` no longer gates the form for them.
 * </p>
 *
 * <p>
 * The synthetic `Booking` option on the create form is NOT a
 * `TaskTypeValue` and never reaches here: it posts through
 * `CreateOrderBookingTaskInput`, which accepts no cargo items, and spawns
 * a BookingDeparture / BookingArrival pair. Those two ARE real task types
 * and do carry the unit row once they exist, editable on the task panel.
 * </p>
 */
export function taskTypeOwnsCargoItems(
  family: CargoFamily,
  taskType: TaskTypeValue,
): boolean {
  if (UNIT_IDENTIFIER_FAMILIES.has(family)) return true;
  return CARGO_HANDLING_TASK_TYPES.has(taskType);
}

export function cargoItemKindFor(family: CargoFamily): CargoItemKindValue {
  if (family === 'container') return 'ContainerUnit';
  if (family === 'tank') return 'TankUnit';
  return 'PalletLine';
}

/**
 * Parallel rows to render for (family, perTrailer). Container / tank
 * double up to perTrailer (one row per physical unit); pallet stays at
 * 1 row.
 */
export function cargoItemRowCountFor(family: CargoFamily, perTrailer: number): number {
  if (family === 'container' || family === 'tank') {
    return Math.max(1, Math.min(perTrailer, 2));
  }
  return 1;
}

/**
 * Whether a cargo-handling task is a load-side ('load') or delivery-side
 * ('delivery') event. Swaps Seal ↔ Seal Status and Weight ↔ Offload on
 * the container expanded form. Non-cargo-handling types default to
 * 'load' — the inputs section won't render for them anyway.
 */
export function cargoHandlingPhaseFor(taskType: TaskTypeValue): 'load' | 'delivery' {
  switch (taskType) {
    case 'PickUp':
    case 'Load':
    case 'Collect':
      return 'load';
    case 'DropOff':
    case 'Unload':
    case 'Deliver':
      return 'delivery';
    default:
      return 'load';
  }
}
